import React from 'react';
import MuiInput from '@material-ui/core/Input';
import Icon from '@material-ui/core/Icon';
import { makeStyles } from '@material-ui/styles';
import InputAdornment from '@material-ui/core/InputAdornment';
import LinearProgress from '@material-ui/core/LinearProgress';
import PropTypes from 'prop-types';
/* eslint-disable camelcase */
import {
  primary1000,
  secondary1000,
  secondary300,
  accentTwo1000,
  accentThree1000,
  disabledBG,
  disabledText,
  secondary200,
} from '../../../src/themes/colors';
/* eslint-enable   camelcase */

const inputState = {
  info: secondary300,
  warning: accentTwo1000,
  error: accentThree1000,
  success: primary1000,
};
const useStyles = makeStyles(theme => ({
  root: props => ({
    /* eslint-disable-next-line no-confusing-arrow */
    height: props.multiline ? 'auto' : '3rem !important',
    border: props.noBorder ? 'none' : `0.063rem solid ${secondary300}`,
    borderRadius: '0.375rem !important',
    overflow: 'hidden',
    padding: '0rem',
    backgroundColor: theme.palette.white,
    '&:hover, &:focus, &:focus-within, &:active': {
      border: props.noBorder
        ? 'none'
        : `0.063rem solid ${props.inputProps && props.inputProps.disabled ? secondary300 : primary1000}`,
    },
  }),
  input: {
    paddingLeft: '0.75rem !important',
    paddingRight: '0.75rem !important',
    height: 'auto',
    color: `${theme.palette.secondary.text} !important`,
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.875rem !important',
    border: 'none !important',
    boxShadow: 'none !important',
    '&:focus': {
      border: 'none !important',
      boxShadow: 'none !important',
    },
  },
  adornmentLeftRoot: {
    paddingLeft: '0.75rem',
    color: secondary300,
    margin: 0,
  },
  adornmentRightRoot: {
    paddingRight: '0.75rem',
    color: secondary300,
    margin: 0,
  },
  disabled: {
    border: 'none !important',
    backgroundColor: `${disabledBG} !important`,
    color: disabledText,
    borderRadius: '0.375rem',
    '&:hover': {
      border: 'none !important',
    },
    '&:active': {
      border: 'none',
    },
  },
  messageWrapper: {
    marginTop: '0.25rem',
    display: 'flex',
  },
  IconRoot: {
    fontSize: '1rem !important',
    marginRight: '0.375rem',
    color: props => inputState[props.messageType],
  },
  message: {
    display: 'inline',
    color: secondary1000,
    fontSize: '0.75rem',
    lineHeight: '1rem',
  },
  progressBarRoot: {
    margin: '0.25rem 0',
    borderRadius: '0.125rem',
  },
  colorPrimary: {
    backgroundColor: secondary200,
  },
  barColorPrimary: {
    /* eslint-disable-next-line no-confusing-arrow */
    backgroundColor: props => props.progress < 100 ?
      inputState.warning : inputState.success,
    borderRadius: '0.125rem',
  },
  inputMultiline: {
    /* eslint-disable-next-line no-confusing-arrow */
    resize: props => props.resize ? 'auto' : 'none',
  },
}));

const Input = (props) => {
  const classes = useStyles(props);
  const messageIcon = props.messageType === 'success' ? 'check' : 'error_outline_rounded';
  return (
    <React.Fragment>
      <MuiInput
        {...props}
        classes={{
          root: classes.root,
          input: classes.input,
          disabled: classes.disabled,
          inputMultiline: classes.inputMultiline,
        }}
        placeholder={props.placeholder}
        disableUnderline
        startAdornment={props.leftIcon ?
          <InputAdornment position="start" classes={{ root: classes.adornmentLeftRoot }}>
            <Icon>{props.leftIcon}</Icon>
          </InputAdornment> : null
        }
        endAdornment={props.rightIcon ?
          <InputAdornment position="start" classes={{ root: classes.adornmentRightRoot }}>
            <Icon>{props.rightIcon}</Icon>
          </InputAdornment> : null
        }
        fullWidth
      />
      {props.type === 'password' &&
        <LinearProgress
          classes={{
            root: classes.progressBarRoot,
            colorPrimary: classes.colorPrimary,
            barColorPrimary: classes.barColorPrimary,
          }}
          variant="determinate"
          value={props.progress}
        />
      }
      {props.message &&
        <div className={classes.messageWrapper}>
          <Icon classes={{ root: classes.IconRoot }}>{messageIcon}</Icon>
          <div className={classes.message}>{props.message}</div>
        </div>
      }
    </React.Fragment>);
};

Input.propTypes = {
  disabled: PropTypes.bool,
  messageType: PropTypes.string,
  message: PropTypes.string,
  classes: PropTypes.element.isRequired,
};

Input.defaultProps = {
  disabled: false,
  messageType: 'info',
  message: null,
};

export default Input;
